/**
 * GET /chat/document-export-suggestions — personalized export CTA copy for one session document (GRO-2885).
 */
import { useAuthToken } from '@/hooks/useAuthToken';
import { isChatDocumentExportSuggestionsApiAvailable } from '@/utils/backendSurfaces';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import type { components } from '@heidi/schema/ml-scribe';
import { useQuery } from '@tanstack/react-query';

export type DocumentExportSuggestionResponse =
  components['schemas']['DocumentExportSuggestionResponse'];

export const DOCUMENT_EXPORT_SUGGESTIONS_QUERY_KEY = 'document-export-suggestions';

async function getDocumentExportSuggestions(
  sessionId: string,
  documentId: string
): Promise<DocumentExportSuggestionResponse> {
  const { data } = await scribeHttpV2.get<DocumentExportSuggestionResponse>(
    '/chat/document-export-suggestions',
    { params: { session_id: sessionId, document_id: documentId } }
  );
  return data;
}

interface UseQueryDocumentExportSuggestionsOptions {
  sessionId: string;
  documentId: string | null | undefined;
  enabled?: boolean;
}

export function useQueryDocumentExportSuggestions({
  sessionId,
  documentId,
  enabled = true,
}: UseQueryDocumentExportSuggestionsOptions) {
  const token = useAuthToken();

  return useQuery<DocumentExportSuggestionResponse>({
    queryKey: [DOCUMENT_EXPORT_SUGGESTIONS_QUERY_KEY, sessionId, documentId],
    queryFn: () => getDocumentExportSuggestions(sessionId, documentId as string),
    // The route stays in ml-scribe's cloud-only chat router, so on an on-prem
    // build the in-session banner that mounts this read must not ask.
    enabled:
      isChatDocumentExportSuggestionsApiAvailable &&
      Boolean(token && sessionId && documentId && enabled),
    staleTime: Infinity,
  });
}
